import { Bus, Locate } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, type Column } from '@/components/ui/Table';
import { cn } from '@/lib/utils';
import type { TrackedVehicle } from '@/types/transport';
import { ROUTE_TONE, TRIP_STATUS, toClockLabel } from '../constants';
import { useRegister } from '../useRegister';
import { RegisterToolbar } from './RegisterToolbar';

/** What a status in the address may be — every one this register's rows can hold. */
const TRIP_STATUSES = Object.keys(TRIP_STATUS);

export interface ActiveVehiclesPanelProps {
  vehicles: TrackedVehicle[];
  onOpenVehicle?: (vehicle: TrackedVehicle) => void;
  onLocateVehicle?: (vehicle: TrackedVehicle) => void;
}

/**
 * What is on the road, in words rather than on the plate.
 *
 * The map answers "where is everything"; this answers "where is *that* one, and
 * when did we last hear from it". The last-updated column is the one that
 * matters when a device stops reporting — a bus frozen on the map looks the
 * same as a bus standing still, and only the timestamp tells them apart.
 *
 * No paging: this is the fleet that is moving right now, which is a dozen rows
 * at a large school and always fits.
 */
export function ActiveVehiclesPanel({
  vehicles,
  onOpenVehicle,
  onLocateVehicle,
}: ActiveVehiclesPanelProps) {
  const register = useRegister({
    prefix: 'active',
    statuses: TRIP_STATUSES,
    rows: vehicles,
    fieldsOf: (vehicle) => [
      vehicle.registrationNumber,
      vehicle.driverName,
      vehicle.routeName,
      vehicle.currentLocation,
    ],
    statusOf: (vehicle) => vehicle.status,
  });

  const columns: Column<TrackedVehicle>[] = [
    {
      key: 'vehicle',
      header: 'Vehicle no.',
      render: (vehicle) => (
        <span className="inline-flex items-center gap-2.5">
          <span
            className={cn(
              'grid h-8 w-8 shrink-0 place-items-center rounded-xl',
              ROUTE_TONE[vehicle.tone].soft,
            )}
            aria-hidden
          >
            <Bus className="h-4 w-4" />
          </span>
          <span className="font-bold text-ink">{vehicle.registrationNumber}</span>
        </span>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      render: (vehicle) => (
        <span className={cn('pill text-[12px] font-bold tracking-label', ROUTE_TONE[vehicle.tone].soft)}>
          {vehicle.routeCode}
        </span>
      ),
    },
    {
      key: 'driver',
      header: 'Driver',
      hideOnMobile: true,
      render: (vehicle) => <span className="font-semibold text-ink-2">{vehicle.driverName}</span>,
    },
    {
      key: 'location',
      header: 'Current location',
      render: (vehicle) => (
        <span className="font-semibold text-ink-2">{vehicle.currentLocation}</span>
      ),
    },
    {
      key: 'updated',
      header: 'Last updated',
      hideOnMobile: true,
      render: (vehicle) => (
        <span className="font-semibold text-ink-2 whitespace-nowrap">{toClockLabel(vehicle.lastUpdated)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (vehicle) => (
        <Badge tone={TRIP_STATUS[vehicle.status].tone} withDot>
          {TRIP_STATUS[vehicle.status].label}
        </Badge>
      ),
    },
    {
      key: 'eta',
      header: 'ETA to next stop',
      hideOnMobile: true,
      render: (vehicle) => (
        <span className="font-bold text-ink">{vehicle.etaMinutes} min</span>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      render: (vehicle) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => onOpenVehicle?.(vehicle)}
            title={`Open ${vehicle.registrationNumber}`}
            aria-label={`Open ${vehicle.registrationNumber}`}
            className="grid h-8 w-8 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-2 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Bus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onLocateVehicle?.(vehicle)}
            title={`Centre the map on ${vehicle.registrationNumber}`}
            aria-label={`Centre the map on ${vehicle.registrationNumber}`}
            className="grid h-8 w-8 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-2 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Locate className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-line bg-surface p-4 shadow-sm">
      <RegisterToolbar
        title="Active vehicles"
        searchPlaceholder="Search vehicle or driver…"
        register={register}
        statusOptions={[
          { value: 'ON_ROUTE', label: TRIP_STATUS.ON_ROUTE.label },
          { value: 'DELAYED', label: TRIP_STATUS.DELAYED.label },
          { value: 'NOT_STARTED', label: TRIP_STATUS.NOT_STARTED.label },
        ]}
      />

      <div className="-mx-4">
        <Table
          columns={columns}
          rows={register.visible}
          rowKey={(vehicle) => vehicle.uuid}
          variant="plain"
          empty={
            <EmptyState
              title={register.query || register.status ? 'No vehicles match' : 'Nothing on the road'}
              description={
                register.query || register.status
                  ? 'Nothing matches that search or filter. Try another number, driver or route.'
                  : 'Vehicles appear here once a run has started and the device aboard reports in.'
              }
            />
          }
        />
      </div>
    </section>
  );
}
